import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { PlusIcon, CreditCardIcon, SparklesIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { countries } from 'countries-list'
import { Country, State, City } from 'country-state-city'
import HotelRefCard from '../components/HotelRefCard'
import FlightRefCard from '../components/FlightRefCard'
import { buildApiUrl } from '../config/api'


function Redemptions() {
  const { user } = useAuth()
  const [showAddForm, setShowAddForm] = useState(false)
  const [redemptions, setRedemptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loyaltyPrograms, setLoyaltyPrograms] = useState([])
  
  // Helper function to get filtered loyalty programs by type
  const getFilteredPrograms = (type) => {
    // Map form redemption types to database program types
    const typeMapping = {
      'flight': 'airline',
      'hotel': 'hotel'
    }
    const mappedType = typeMapping[type] || type
    return loyaltyPrograms.filter(program => program.program_type === mappedType)
  }
  
  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not set'
    
    // Handle empty objects or invalid date objects
    if (typeof dateString === 'object' && Object.keys(dateString).length === 0) {
      return 'Date not set'
    }
    
    // Handle null values
    if (dateString === null) return 'Date not set'
    
    // Handle undefined values
    if (dateString === undefined) return 'Date not set'
    
    try {
      // Handle both ISO strings and Date objects
      let date
      if (dateString instanceof Date) {
        date = dateString
      } else if (typeof dateString === 'string') {
        // Handle ISO date strings
        date = new Date(dateString)
      } else {
        return 'Date not set'
      }
      
      if (isNaN(date.getTime())) {
        return 'Date not set'
      }
      
      // Check if the date is valid (not too far in the past or future)
      const now = new Date()
      const yearDiff = Math.abs(now.getFullYear() - date.getFullYear())
      if (yearDiff > 100) {
        return 'Date not set'
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return 'Date not set'
    }
  }
  
  const [formData, setFormData] = useState({
    redemption_type: 'flight',
    program_id: '', // Will be set after programs are fetched
    points_spent: '',
    cash_price: '',
    tax_fees: '',
    currency_code: 'USD',
    award_type: 'saver',
    booking_date: new Date().toISOString().split('T')[0],
    travel_start_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    // Flight-specific fields
    origin_iata: '',
    destination_iata: '',
    operating_carrier_id: '',
    cabin_class: 'Economy',
    trip_type: 'one_way',
    num_passengers: 1,
    flight_number: '',
    return_date: '', // Legacy field for backward compatibility
    // Multi-city segments
    segments: [
      {
        origin_iata: '',
        destination_iata: '',
        travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        operating_carrier_id: '',
        flight_number: '',
        cabin_class: 'Economy'
      }
    ],
    // Hotel-specific fields
    hotel_name: '',
    city: '',
    country: '',
    check_in_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    check_out_date: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    room_type: '',
    brand: '',
    hotel_group: ''
  })

  // Country search state
  const [countrySearch, setCountrySearch] = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [filteredCountries, setFilteredCountries] = useState([])
  const [selectedCountryIndex, setSelectedCountryIndex] = useState(-1)

  // City search state
  const [citySearch, setCitySearch] = useState('')
  const [showCityDropdown, setShowCityDropdown] = useState(false)
  const [filteredCities, setFilteredCities] = useState([])
  const [selectedCityIndex, setSelectedCityIndex] = useState(-1)

  // Hotel search state
  const [hotelSearch, setHotelSearch] = useState('')
  const [showHotelDropdown, setShowHotelDropdown] = useState(false)
  const [filteredHotels, setFilteredHotels] = useState([])
  const [selectedHotelIndex, setSelectedHotelIndex] = useState(-1)

  // Convert countries object to array of country names
  const allCountries = Object.values(countries).map(country => country.name).sort()
  
  // Get cities for a specific country using the country-state-city library
  const getCitiesForCountry = (countryName) => {
    if (!countryName) return []
    
    try {
      // Find the country by name (case-insensitive)
      const country = Country.getAllCountries().find(
        c => c.name.toLowerCase() === countryName.toLowerCase()
      )
      
      if (!country) {
        // console.log('Country not found:', countryName)
        return []
      }
      
      // Get all states/provinces for this country
      const states = State.getStatesOfCountry(country.isoCode)
      
      // Get cities from all states
      const allCities = []
      states.forEach(state => {
        const cities = City.getCitiesOfState(country.isoCode, state.isoCode)
        cities.forEach(city => {
          allCities.push(city.name)
        })
      })
      
      // Remove duplicates and sort
      const uniqueCities = [...new Set(allCities)].sort()
      // console.log('Found ${uniqueCities.length} cities for ${countryName}')
      
      return uniqueCities
    } catch (error) {
      // console.error('Error getting cities for country:', error)
      return []
    }
  }

  // Fetch hotels from backend
  const fetchHotels = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2 || !formData.country || !formData.city) {
      setFilteredHotels([])
      return
    }

    try {
      const response = await fetch(buildApiUrl('/api/hotels?search=${encodeURIComponent(searchTerm.trim())}&country=${encodeURIComponent(formData.country)}&city=${encodeURIComponent(formData.city)}'))
      if (response.ok) {
        const data = await response.json()
        setFilteredHotels(data.hotels || [])
      } else {
        // console.error('Failed to fetch hotels')
        setFilteredHotels([])
      }
    } catch (error) {
      // console.error('Error fetching hotels:', error)
      setFilteredHotels([])
    }
  }



  const sampleRedemptions = [
    {
      id: 1,
      program: 'American Airlines AAdvantage',
      type: 'Flight',
      points: 25000,
      cashValue: 500,
      cpp: 0.020,
      date: '2024-01-15',
      route: 'LAX → JFK',
      class: 'Economy'
    },
    {
      id: 2,
      program: 'Marriott Bonvoy',
      type: 'Hotel',
      points: 35000,
      cashValue: 350,
      cpp: 0.010,
      date: '2024-01-10',
      location: 'New York',
      nights: 2
    },
    {
      id: 3,
      program: 'Delta SkyMiles',
      type: 'Flight',
      points: 40000,
      cashValue: 800,
      cpp: 0.020,
      date: '2024-01-05',
      route: 'SFO → LHR',
      class: 'Premium Economy'
    }
  ]

  // Fetch redemptions from backend
  const fetchRedemptions = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/api/redemptions'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setRedemptions(data.redemptions || [])
      } else {
        // console.error('Failed to fetch redemptions')
      }
    } catch (error) {
      // console.error('Error fetching redemptions:', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch loyalty programs from backend
  const fetchLoyaltyPrograms = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/loyalty-programs'))
      if (response.ok) {
        const data = await response.json()
        setLoyaltyPrograms(data.programs || [])
        // Set default program to first available flight program
        if (data.programs && data.programs.length > 0) {
          const flightPrograms = getFilteredPrograms('flight')
          if (flightPrograms.length > 0) {
            setFormData(prev => ({
              ...prev,
              program_id: flightPrograms[0].id
            }))
          }
        }
      } else {
        // console.error('Failed to fetch loyalty programs')
      }
    } catch (error) {
      // console.error('Error fetching loyalty programs:', error)
    }
  }

  // Load redemptions when component mounts or user changes
  useEffect(() => {
    fetchRedemptions()
    fetchLoyaltyPrograms()
  }, [user])

  // Sync citySearch with formData.city when formData.city changes
  useEffect(() => {
    if (formData.city && !citySearch) {
      setCitySearch(formData.city)
    }
  }, [formData.city, citySearch])

  // Handle trip type changes and ensure proper segment count
  useEffect(() => {
    if (formData.trip_type === 'round_trip') {
      // Ensure round trip has exactly 2 segments
      if (formData.segments.length !== 2) {
        setFormData(prev => ({
          ...prev,
          segments: [
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: prev.travel_start_date,
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            },
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: prev.travel_start_date,
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            }
          ]
        }))
      }
    } else if (formData.trip_type === 'multi_city') {
      // Ensure multi-city has at least 1 segment
      if (formData.segments.length === 0) {
        setFormData(prev => ({
          ...prev,
          segments: [{
            origin_iata: '',
            destination_iata: '',
            travel_date: prev.travel_start_date,
            operating_carrier_id: '',
            flight_number: '',
            cabin_class: 'Economy'
          }]
        }))
      }
    }
  }, [formData.trip_type])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      }
      
      // If redemption type changes, filter loyalty programs and update program_id
      if (name === 'redemption_type') {
        const filteredPrograms = getFilteredPrograms(value)
        if (filteredPrograms.length > 0) {
          newFormData.program_id = filteredPrograms[0].id
        } else {
          newFormData.program_id = ''
        }
      }
      
      return newFormData
    })
  }

  // Handle country search
  const handleCountrySearch = (value) => {
    setCountrySearch(value)
    if (value.trim() === '') {
      setFilteredCountries([])
      setShowCountryDropdown(false)
      setSelectedCountryIndex(-1)
      return
    }
    
    const filtered = allCountries.filter(country => 
      country.toLowerCase().includes(value.toLowerCase())
    )
    setFilteredCountries(filtered)
    setShowCountryDropdown(true)
    setSelectedCountryIndex(-1) // Reset selection when filtering
  }

  // Handle country selection
  const handleCountrySelect = (countryName) => {
    setFormData(prev => ({ ...prev, country: countryName }))
    setCountrySearch(countryName)
    setShowCountryDropdown(false)
    setFilteredCountries([])
    setSelectedCountryIndex(-1)
    
    // Reset city when country changes
    setFormData(prev => ({ ...prev, city: '' }))
    setCitySearch('')
    setShowCityDropdown(false)
    setFilteredCities([])
    setSelectedCityIndex(-1)
    
    // Reset hotel when country changes
    setFormData(prev => ({ ...prev, hotel_name: '' }))
    setHotelSearch('')
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
  }

  // Handle keyboard navigation for country dropdown
  const handleCountryKeyDown = (e) => {
    if (!showCountryDropdown || filteredCountries.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedCountryIndex(prev => 
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedCountryIndex(prev => 
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedCountryIndex >= 0 && selectedCountryIndex < filteredCountries.length) {
          handleCountrySelect(filteredCountries[selectedCountryIndex])
        }
        break
      case 'Escape':
        setShowCountryDropdown(false)
        setSelectedCountryIndex(-1)
        break
    }
  }

  // Handle city search
  const handleCitySearch = (value) => {
    setCitySearch(value)
    // Also update formData.city for form submission
    setFormData(prev => ({ ...prev, city: value }))
    
    if (value.trim() === '') {
      setFilteredCities([])
      setShowCityDropdown(false)
      setSelectedCityIndex(-1)
      return
    }
    
    const citiesForCountry = getCitiesForCountry(formData.country)
    const filtered = citiesForCountry.filter(city => 
      city.toLowerCase().includes(value.toLowerCase())
    )
    setFilteredCities(filtered)
    setShowCityDropdown(true)
    setSelectedCityIndex(-1) // Reset selection when filtering
  }

  // Handle city selection
  const handleCitySelect = (cityName) => {
    setFormData(prev => ({ ...prev, city: cityName }))
    setCitySearch(cityName)
    setShowCityDropdown(false)
    setFilteredCities([])
    setSelectedCityIndex(-1)
    
    // Reset hotel when city changes
    setFormData(prev => ({ ...prev, hotel_name: '' }))
    setHotelSearch('')
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
  }

  // Handle keyboard navigation for city dropdown
  const handleCityKeyDown = (e) => {
    if (!showCityDropdown || filteredCities.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedCityIndex(prev => 
          prev < filteredCities.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedCityIndex(prev => 
          prev > 0 ? prev - 1 : filteredCities.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedCityIndex >= 0 && selectedCityIndex < filteredCities.length) {
          handleCitySelect(filteredCities[selectedCityIndex])
        }
        break
      case 'Escape':
        setShowCityDropdown(false)
        setSelectedCityIndex(-1)
        break
    }
  }

  // Handle hotel search
  const handleHotelSearch = (value) => {
    setHotelSearch(value)
    setFormData(prev => ({ ...prev, hotel_name: value }))
    
    if (value.trim().length >= 2 && formData.country && formData.city) {
      fetchHotels(value)
      setShowHotelDropdown(true)
    } else {
      setFilteredHotels([])
      setShowHotelDropdown(false)
    }
    setSelectedHotelIndex(-1)
  }

  // Handle hotel selection
  const handleHotelSelect = (hotel) => {
    setFormData(prev => ({ 
      ...prev, 
      hotel_name: hotel.name,
      city: hotel.city,
      country: hotel.country,
      brand: hotel.brand || '',
      hotel_group: hotel.hotel_group || ''
    }))
    setHotelSearch(hotel.name)
    setShowHotelDropdown(false)
    setFilteredHotels([])
    setSelectedHotelIndex(-1)
    
    // Also update city and country search states
    setCitySearch(hotel.city)
    setCountrySearch(hotel.country)
  }

  // Handle keyboard navigation for hotel dropdown
  const handleHotelKeyDown = (e) => {
    if (!showHotelDropdown || filteredHotels.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedHotelIndex(prev => 
          prev < filteredHotels.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedHotelIndex(prev => 
          prev > 0 ? prev - 1 : filteredHotels.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedHotelIndex >= 0 && selectedHotelIndex < filteredHotels.length) {
          handleHotelSelect(filteredHotels[selectedHotelIndex])
        }
        break
      case 'Escape':
        setShowHotelDropdown(false)
        setSelectedHotelIndex(-1)
        break
    }
  }

  const handleSegmentChange = (index, field, value) => {
    setFormData(prev => {
      const newSegments = prev.segments.map((segment, i) => 
        i === index ? { ...segment, [field]: value } : segment
      )
      
      // Auto-fill for round trip: when segment 1 origin/destination changes, update segment 2
      if (prev.trip_type === 'round_trip' && index === 0) {
        if (field === 'origin_iata') {
          // When departure origin changes, update return destination
          newSegments[1] = { ...newSegments[1], destination_iata: value }
        } else if (field === 'destination_iata') {
          // When departure destination changes, update return origin
          newSegments[1] = { ...newSegments[1], origin_iata: value }
        }
      }
      
      return {
        ...prev,
        segments: newSegments
      }
    })
  }

  const addSegment = () => {
    setFormData(prev => ({
      ...prev,
      segments: [
        ...prev.segments,
        {
          origin_iata: '',
          destination_iata: '',
          travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          operating_carrier_id: '',
          flight_number: '',
          cabin_class: 'Economy'
        }
      ]
    }))
  }

  const removeSegment = (index) => {
    if (formData.segments.length > 1) {
      setFormData(prev => ({
        ...prev,
        segments: prev.segments.filter((_, i) => i !== index)
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!user) {
      alert('Please log in to add redemptions')
      return
    }

    // Get program_id from the form
    let program_id = formData.program_id
    
    if (!program_id) {
      alert('Please select a loyalty program')
      return
    }

    // Validate hotel-specific fields
    if (formData.redemption_type === 'hotel') {
      if (!formData.hotel_name || !formData.city || !formData.country || !formData.check_in_date || !formData.check_out_date) {
        alert('Please fill in all required hotel fields: Hotel Name, City, Country, Check-in Date, and Check-out Date')
        return
      }
      
      // Validate dates
      const checkIn = new Date(formData.check_in_date)
      const checkOut = new Date(formData.check_out_date)
      if (checkOut <= checkIn) {
        alert('Check-out date must be after check-in date')
        return
      }
    }

    const submissionData = {
      ...formData,
      program_id,
      // Convert points_spent to number
      points_spent: parseInt(formData.points_spent) || 0,
      // Convert tax_fees to number or 0 if empty
      tax_fees: parseFloat(formData.tax_fees) || 0,
      // Convert cash_price to number or 0 if empty
      cash_price: formData.cash_price ? parseFloat(formData.cash_price) : 0,
      // Add return_date if it exists (for legacy round trips)
      return_date: formData.return_date || null,
      // Add segments for multi-city
      segments: formData.segments,
      // Add hotel-specific data
      hotel_name: formData.hotel_name || null,
      city: formData.city || null,
      country: formData.country || null,
      check_in_date: formData.check_in_date || null,
      check_out_date: formData.check_out_date || null,
      room_type: formData.room_type || null,
      brand: formData.brand || null,
      hotel_group: formData.hotel_group || null
    }





    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/api/redemptions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      })

      if (response.ok) {
        alert('Redemption added successfully!')
        setShowAddForm(false)
        setFormData({
          redemption_type: 'flight',
          program_id: (() => {
            const flightPrograms = getFilteredPrograms('flight')
            return flightPrograms.length > 0 ? flightPrograms[0].id : ''
          })(),
          points_spent: '',
          cash_price: '',
          tax_fees: '',
          currency_code: 'USD',
          award_type: 'saver',
          booking_date: new Date().toISOString().split('T')[0],
          travel_start_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          // Flight-specific fields
          origin_iata: '',
          destination_iata: '',
          operating_carrier_id: '',
          cabin_class: 'Economy',
          trip_type: 'one_way',
          num_passengers: 1,
          flight_number: '',
          return_date: '', // Reset return_date
          // Reset segments
          segments: [
            {
              origin_iata: '',
              destination_iata: '',
              travel_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              operating_carrier_id: '',
              flight_number: '',
              cabin_class: 'Economy'
            }
          ],
          // Reset hotel-specific fields
          hotel_name: '',
          city: '',
          country: '',
          check_in_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          check_out_date: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          room_type: '',
          brand: '',
          hotel_group: ''
        })
        
        // Reset country search state
        setCountrySearch('')
        setShowCountryDropdown(false)
        setFilteredCountries([])
        
        // Reset city search state
        setCitySearch('')
        setShowCityDropdown(false)
        setFilteredCities([])
        
        // Reset hotel search state
        setHotelSearch('')
        setShowHotelDropdown(false)
        setFilteredHotels([])
        setSelectedHotelIndex(-1)
        
        // Refresh the redemptions list instead of reloading the page
        fetchRedemptions()
      } else {
        const error = await response.json()
        alert('Error: ${error.message}')
      }
    } catch (error) {
      // console.error('Error adding redemption:', error)
      alert('Failed to add redemption. Please try again.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Redemptions</h1>
          <p className="text-gray-600">
            {user ? 'Track and manage your loyalty program redemptions' : 'Explore sample redemptions and see how to track your own'}
          </p>
        </div>
        
        {user ? (
          <button 
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Redemption</span>
          </button>
        ) : (
          <Link to="/register" className="btn-primary flex items-center space-x-2">
            <SparklesIcon className="w-5 h-5" />
            <span>Start Tracking</span>
          </Link>
        )}
      </div>

      {/* Guest User Banner */}
      {!user && (
        <div className="bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-xl p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center">
              <CreditCardIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Track Your Redemptions
              </h3>
              <p className="text-gray-600 mb-3">
                Log every points redemption to analyze your CPP, track value over time, and get insights on how to maximize your loyalty program value.
              </p>
              <div className="flex space-x-3">
                <Link to="/register" className="btn-primary">
                  Create Free Account
                </Link>
                <Link to="/chatbot" className="btn-secondary">
                  Ask AI About Redemptions
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Redemption Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Add New Redemption</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Redemption Type *
                    </label>
                    <select
                      name="redemption_type"
                      value={formData.redemption_type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500"
                    >
                      <option value="flight">Flight</option>
                      <option value="hotel">Hotel</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loyalty Program *
                    </label>
                    <select
                      name="program_id"
                      value={formData.program_id}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Program</option>
                      {getFilteredPrograms(formData.redemption_type)
                        .map(program => (
                          <option key={program.id} value={program.id}>{program.name}</option>
                        ))}
                    </select>
                  </div>
                </div>
                
                <div className="text-xs text-blue-600 mb-2">
                  {formData.redemption_type === 'flight' 
                    ? 'Flight-specific fields will appear below' 
                    : 'Hotel fields will appear below'}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Points Spent *
                    </label>
                    <input
                      type="number"
                      name="points_spent"
                      value={formData.points_spent}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., 25000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Cash Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="cash_price"
                      value={formData.cash_price}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., 500.00 (optional)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Current market price (optional)
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax & Fees
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="tax_fees"
                      value={formData.tax_fees || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., 25.00 (optional)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      name="currency_code"
                      value={formData.currency_code}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                    </select>
                  </div>
                </div>


                
                {/* Flight-specific fields */}
                {formData.redemption_type === 'flight' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Trip Type *
                      </label>
                      <select
                        name="trip_type"
                        value={formData.trip_type}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="one_way">One Way</option>
                        <option value="round_trip">Round Trip</option>
                        <option value="multi_city">Multi-City</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.trip_type === 'multi_city' 
                          ? 'You can add multiple destinations for complex itineraries'
                          : formData.trip_type === 'round_trip'
                          ? 'Round trip with departure and return segments'
                          : 'Select your travel pattern'}
                      </p>
                    </div>
                    
                    {/* Only show main airport fields for one-way trips */}
                    {formData.trip_type === 'one_way' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Origin Airport *
                            </label>
                            <input
                              type="text"
                              name="origin_iata"
                              value={formData.origin_iata}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g., LAX"
                              maxLength="3"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Use 3-letter airport code (e.g., LAX, JFK, SFO)
                            </p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Destination Airport *
                            </label>
                            <input
                              type="text"
                              name="destination_iata"
                              value={formData.destination_iata}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g., JFK"
                              maxLength="3"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Use 3-letter airport code (e.g., LAX, JFK, SFO)
                            </p>
                          </div>
                        </div>
                        

                      </>
                    )}
                    
                    {/* Flight segments for multi-city and round-trip */}
                    {(formData.trip_type === 'multi_city' || formData.trip_type === 'round_trip') && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-700">Flight Segments</h4>
                          {formData.trip_type === 'multi_city' && (
                            <button
                              type="button"
                              onClick={addSegment}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              <PlusIcon className="w-4 h-4 mr-1" />
                              Add Segment
                            </button>
                          )}
                        </div>
                        
                        {formData.segments.map((segment, index) => (
                          <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-sm font-medium text-gray-700">
                                {formData.trip_type === 'round_trip' 
                                  ? (index === 0 ? 'Departure' : 'Return')
                                  : 'Segment ${index + 1}'
                                }
                              </h5>
                              {formData.trip_type === 'multi_city' && formData.segments.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeSegment(index)}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Origin *
                                </label>
                                <input
                                  type="text"
                                  value={segment.origin_iata}
                                  onChange={(e) => handleSegmentChange(index, 'origin_iata', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="LAX"
                                  maxLength="3"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Destination *
                                </label>
                                <input
                                  type="text"
                                  value={segment.destination_iata}
                                  onChange={(e) => handleSegmentChange(index, 'destination_iata', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="JFK"
                                  maxLength="3"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Travel Date *
                                </label>
                                <input
                                  type="date"
                                  value={segment.travel_date}
                                  onChange={(e) => handleSegmentChange(index, 'travel_date', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Operating Carrier *
                                </label>
                                <input
                                  type="text"
                                  value={segment.operating_carrier_id}
                                  onChange={(e) => handleSegmentChange(index, 'operating_carrier_id', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="AA"
                                  maxLength="2"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Flight Number *
                                </label>
                                <input
                                  type="text"
                                  value={segment.flight_number}
                                  onChange={(e) => handleSegmentChange(index, 'flight_number', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  placeholder="123"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Cabin Class *
                                </label>
                                <select
                                  value={segment.cabin_class}
                                  onChange={(e) => handleSegmentChange(index, 'cabin_class', e.target.value)}
                                  required
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                  <option value="Economy">Economy</option>
                                  <option value="Premium_Economy">Premium Economy</option>
                                  <option value="Business">Business</option>
                                  <option value="First">First</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Passengers field for all flight types */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Passengers *
                          </label>
                          <input
                            type="number"
                            name="num_passengers"
                            value={formData.num_passengers}
                            onChange={handleInputChange}
                            required
                            min="1"
                            max="10"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="1"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Only show main flight fields for one-way trips */}
                    {formData.trip_type === 'one_way' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Operating Carrier *
                            </label>
                            <input
                              type="text"
                              name="operating_carrier_id"
                              value={formData.operating_carrier_id}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g., AA"
                              maxLength="2"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Use 2-letter airline code (e.g., AA, DL, UA)
                            </p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Flight Number *
                            </label>
                            <input
                              type="text"
                              name="flight_number"
                              value={formData.flight_number}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g., 123"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Flight number (e.g., 123, 4567)
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Cabin Class *
                            </label>
                            <select
                              name="cabin_class"
                              value={formData.cabin_class}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="Economy">Economy</option>
                              <option value="Premium_Economy">Premium Economy</option>
                              <option value="Business">Business</option>
                              <option value="First">First</option>
                            </select>
                          </div>
                          
                          {/* Travel Date - only show for One Way trips */}
                          {formData.trip_type === 'one_way' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Travel Date *
                              </label>
                              <input
                                type="date"
                                name="travel_start_date"
                                value={formData.travel_start_date}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Hotel-specific fields */}
                {formData.redemption_type === 'hotel' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={countrySearch}
                            onChange={(e) => handleCountrySearch(e.target.value)}
                            onKeyDown={handleCountryKeyDown}
                            onFocus={() => {
                              if (countrySearch.trim() !== '') {
                                setShowCountryDropdown(true)
                                const filtered = allCountries.filter(country => 
                                  country.toLowerCase().includes(countrySearch.trim().toLowerCase())
                                )
                                setFilteredCountries(filtered)
                              }
                            }}
                            placeholder="Search for a country..."
                            required
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                        </div>
                        
                        {/* Country dropdown */}
                        {showCountryDropdown && filteredCountries.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredCountries.map((country, index) => (
                              <div
                                key={index}
                                                                  className={'px-3 py-2 cursor-pointer text-sm ' + (
                                    index === selectedCountryIndex 
                                      ? 'bg-primary-500 text-white' 
                                      : 'hover:bg-gray-100'
                                  )}
                                onClick={() => handleCountrySelect(country)}
                                onMouseEnter={() => setSelectedCountryIndex(index)}
                              >
                                {country}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500 mt-1">
                          Type to search countries (e.g., "jap" for Japan, "united" for United States)
                        </p>
                      </div>
                      
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={citySearch || formData.city}
                            onChange={(e) => handleCitySearch(e.target.value)}
                            onKeyDown={handleCityKeyDown}
                            onFocus={() => {
                              if (citySearch.trim() !== '' && formData.country) {
                                setShowCityDropdown(true)
                                const citiesForCountry = getCitiesForCountry(formData.country)
                                const filtered = citiesForCountry.filter(city => 
                                  city.toLowerCase().includes(citySearch.toLowerCase())
                                )
                                setFilteredCities(filtered)
                              }
                            }}
                            placeholder={formData.country ? "Search for a city..." : "Select country first"}
                            required
                            disabled={!formData.country}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                          <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                        </div>
                        
                        {/* City dropdown */}
                        {showCityDropdown && filteredCities.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredCities.map((city, index) => (
                              <div
                                key={index}
                                                                  className={'px-3 py-2 cursor-pointer text-sm ' + (
                                    index === selectedCityIndex 
                                      ? 'bg-primary-500 text-white' 
                                      : 'hover:bg-gray-100'
                                  )}
                                onClick={() => handleCitySelect(city)}
                                onMouseEnter={() => setSelectedCityIndex(index)}
                              >
                                {city}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.country 
                            ? "Type to search cities in the selected country" 
                            : "Please select a country first"
                          }
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hotel Name *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={hotelSearch || formData.hotel_name}
                            onChange={(e) => handleHotelSearch(e.target.value)}
                            onKeyDown={handleHotelKeyDown}
                            onFocus={() => {
                              if (hotelSearch.trim().length >= 2 && formData.country && formData.city) {
                                fetchHotels(hotelSearch)
                                setShowHotelDropdown(true)
                              }
                            }}
                            placeholder={formData.country && formData.city ? "Type to search existing hotels..." : "Select country and city first"}
                            required
                            disabled={!formData.country || !formData.city}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                          <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                        </div>
                        
                        {/* Hotel dropdown */}
                        {showHotelDropdown && filteredHotels.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredHotels.map((hotel, index) => (
                              <div
                                key={hotel.id}
                                                                  className={'px-3 py-2 cursor-pointer text-sm ' + (
                                    index === selectedHotelIndex 
                                      ? 'bg-primary-500 text-white' 
                                      : 'hover:bg-gray-100'
                                  )}
                                onClick={() => handleHotelSelect(hotel)}
                                onMouseEnter={() => setSelectedHotelIndex(index)}
                              >
                                <div className="font-medium">{hotel.name}</div>
                                <div className="text-xs text-gray-500">
                                  {hotel.brand && `${hotel.brand} • `}{hotel.hotel_group && `${hotel.hotel_group}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.country && formData.city 
                            ? "Type to search existing hotels in this location or enter a new one" 
                            : "Please select country and city first"
                          }
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Room Type
                        </label>
                        <input
                          type="text"
                          name="room_type"
                          value={formData.room_type}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="e.g., Standard King (optional)"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Check-in Date *
                        </label>
                        <input
                          type="date"
                          name="check_in_date"
                          value={formData.check_in_date}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Check-out Date *
                        </label>
                        <input
                          type="date"
                          name="check_out_date"
                          value={formData.check_out_date}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Brand
                        </label>
                        <input
                          type="text"
                          name="brand"
                          value={formData.brand}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="e.g., Park Hyatt (optional)"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hotel Group
                        </label>
                        <input
                          type="text"
                          name="hotel_group"
                          value={formData.hotel_group}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="e.g., Hyatt (optional)"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Award Type
                  </label>
                  <select
                    name="award_type"
                    value={formData.award_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="saver">Saver</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Booking Date *
                    </label>
                    <input
                      type="date"
                      name="booking_date"
                      value={formData.booking_date}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  
                </div>
              </form>
            </div>
            
            <div className="flex space-x-3 p-4 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 btn-primary"
              >
                Add Redemption
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample Redemptions */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            {user ? 'Your Redemptions' : 'Sample Redemptions'}
          </h3>
          {!user && (
            <span className="text-sm text-gray-500">Demo data shown</span>
          )}
        </div>
        
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading redemptions...</p>
            </div>
          ) : user && redemptions.length > 0 ? (
            // Show real redemptions from backend
            redemptions.map((redemption) => (
              redemption.hotel ? (
                // For hotels, show only the HotelRefCard
                <HotelRefCard
                  key={redemption.id}
                  hotel={redemption.hotel.hotel}
                  checkInDate={redemption.hotel.check_in_date}
                  checkOutDate={redemption.hotel.check_out_date}
                  price={redemption.cash_price}
                  points={redemption.points_spent}
                  cpp={redemption.value_cpp}
                  addedDate={redemption.booking_date}
                />
              ) : (
                // For flights, show the FlightRefCard
                <FlightRefCard
                  key={redemption.id}
                  flight={redemption.flight}
                  price={redemption.cash_price}
                  points={redemption.points_spent}
                  cpp={redemption.value_cpp}
                  addedDate={redemption.booking_date}
                  loyaltyCurrency={redemption.program?.loyalty_currency}
                />
              )
            ))
          ) : user && redemptions.length === 0 ? (
            // Show empty state for logged-in users
            <div className="text-center py-8">
              <CreditCardIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No redemptions yet. Add your first one above!</p>
            </div>
          ) : (
            // Show sample redemptions for guests
            sampleRedemptions.map((redemption) => (
              <div key={redemption.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <div className="w-5 h-5 text-blue-600">✈️</div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {redemption.route || redemption.program}
                    </p>
                    <p className="text-sm text-gray-500">
                      {redemption.route ? 'UA 123' : redemption.type} • {redemption.date}
                    </p>
                    {redemption.route && (
                      <p className="text-xs text-gray-400">
                        {redemption.class}
                      </p>
                    )}
                    {redemption.location && (
                      <p className="text-xs text-gray-400">{redemption.location} • {redemption.nights} nights</p>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="font-medium text-gray-900">{redemption.points.toLocaleString()} pts</p>
                  <p className="text-sm text-gray-500">${redemption.cashValue}</p>
                  <p className="text-sm font-medium text-primary-600">
                    {redemption.cpp.toFixed(1)}¢ CPP
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {!user && (
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-gray-600 mb-3">
              Ready to track your own redemptions?
            </p>
            <Link to="/register" className="btn-primary">
              Sign Up & Start Tracking
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default Redemptions
